import { Link } from "react-router";
import { IMPORT_FLAG_LABELS, type ImportDecision, type ImportKind, type ImportRecordItem, type ImportRunSummary, type WorkflowItem } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, patch, post } from "../../api";
import { ResetCard } from "../../components/reset-card";
import { useSession } from "../../session";
import { Alert, Badge, Button, Empty, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, Tabs, cx, formatDateTime } from "../../components/ui";

const KIND_LABELS: Record<ImportKind, string> = { contact: "Contacts", job: "Jobs", note: "Notes", task: "Open tasks" };
const DECISION: Record<ImportDecision, { label: string; tone: "ok" | "wait" | "off" | "brand" }> = {
  import: { label: "Import", tone: "ok" },
  merge: { label: "Merge", tone: "wait" },
  skip: { label: "Skip", tone: "off" },
};

/** Settings → JobNimbus review: what Sync read from JobNimbus, to change before Apply. */
export function ImportSettings() {
  const { can } = useSession();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["import-status"], queryFn: () => get<{ jobnimbus: boolean }>("/api/import/status") });
  const runs = useQuery({
    queryKey: ["import-runs"],
    queryFn: () => get<ImportRunSummary[]>("/api/import/runs"),
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === "pulling" || r.status === "committing") ? 2000 : false),
  });
  const latest = runs.data?.[0];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="JobNimbus review"
        description="What Sync read from JobNimbus, record by record (it only reads; nothing in JobNimbus changes). Change what's added, merged, or skipped here, then go back to Sync and click Apply. Anything already added is left alone."
        action={
          <Link to="/settings/sync" className="inline-flex items-center rounded-[var(--radius-control)] bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-deep">
            Back to Sync
          </Link>
        }
      />
      {status.data && !status.data.jobnimbus && <Alert tone="info">The JobNimbus API key isn't set up on this server.</Alert>}
      {runs.isLoading ? <Loading /> : !latest ? <Panel><Empty>Nothing read yet. Click Sync now on Settings → Sync.</Empty></Panel> : <Run run={latest} />}
      {latest && can("records.delete") && (
        <div className="mt-6 flex flex-col gap-2">
          <h2 className="text-sm font-semibold tracking-wide text-danger uppercase">Danger zone</h2>
        <ResetCard
          title="Start over"
          description="Removes everything this import brought in (contacts, jobs, notes, tasks, copied files) and anything added on those jobs since (proposals, appointments, work orders...), then clears the import history so you can pull fresh. Contacts that were already in the CRM stay. Texts, calls, and receipts stay, unlinked."
          previewUrl="/api/import/jobnimbus/reset/preview"
          resetUrl="/api/import/jobnimbus/reset"
          phrase="START OVER"
          button="Remove the imported data"
          done="Done. Everything from JobNimbus is removed; run Sync again when you're ready."
        />
        </div>
      )}
      {runs.data && runs.data.length > 1 && (
        <Panel>
          <PanelHeader title="Earlier imports" />
          <ul className="text-sm">
            {runs.data.slice(1).map((r) => (
              <li key={r.id} className="flex flex-wrap gap-3 border-b border-mist px-5 py-2 last:border-0">
                <span>{formatDateTime(r.startedAt)}</span>
                <span className="text-muted">{r.startedBy}</span>
                <span className="text-muted">{r.status === "done" ? resultLine(r) : r.status === "ready" ? "Not committed" : r.status}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

function resultLine(r: ImportRunSummary) {
  const x = r.results;
  if (!x) return "";
  return [`${x.contact?.created ?? 0} contacts added, ${x.contact?.merged ?? 0} merged`, `${x.job?.created ?? 0} jobs`, `${x.note?.created ?? 0} notes`, `${x.task?.created ?? 0} tasks`].join(" · ");
}

function Run({ run }: { run: ImportRunSummary }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<ImportKind>("contact");
  const refresh = () => qc.invalidateQueries({ queryKey: ["import-runs"] });
  const c = run.counts;

  if (run.status === "pulling") {
    return (
      <Panel className="px-5 py-5">
        <Loading />
        <p className="text-sm text-muted">Pulling from JobNimbus… this takes a minute or two.</p>
      </Panel>
    );
  }
  if (run.status === "failed") return <Alert>{run.error}</Alert>;

  return (
    <>
      <Panel>
        <PanelHeader
          title={run.status === "done" ? "Imported" : run.status === "committing" ? "Importing…" : "Ready to review"}
          description={`Pulled ${formatDateTime(run.pulledAt)}${run.startedBy ? ` by ${run.startedBy}` : ""}.${run.committedAt ? ` Imported ${formatDateTime(run.committedAt)}.` : ""}`}
          action={
            run.status === "ready" && (
              <Link to="/settings/sync" className="text-sm font-semibold text-brand hover:underline">
                Apply on Sync →
              </Link>
            )
          }
        />
        {run.status === "committing" && (
          <div className="px-5">
            <Loading />
          </div>
        )}
        {run.status === "done" && (
          <div className="px-5 pt-4">
            <Alert tone="ok">{resultLine(run)}. Records with a problem are marked below; fix them in JobNimbus or here and run Sync again.</Alert>
          </div>
        )}
        <div className="grid gap-3 px-5 py-4 sm:grid-cols-4">
          {(Object.keys(KIND_LABELS) as ImportKind[]).map((k) => (
            <div key={k} className="rounded-[var(--radius-control)] bg-mist/60 px-4 py-3 text-sm">
              <div className="font-semibold">{KIND_LABELS[k]}</div>
              <div className="text-2xl font-semibold">{c[k].total}</div>
              <div className="text-muted">
                {c[k].decisions.import} import{c[k].decisions.merge ? ` · ${c[k].decisions.merge} merge` : ""} · {c[k].decisions.skip} skip
              </div>
            </div>
          ))}
        </div>
      </Panel>
      {kind === "job" && <Placement run={run} />}
      <Tabs tabs={(Object.keys(KIND_LABELS) as ImportKind[]).map((k) => ({ value: k, label: `${KIND_LABELS[k]} (${c[k].total})` }))} value={kind} onChange={setKind} />
      <Records key={kind} run={run} kind={kind} />
    </>
  );
}

/** Which workflow each JobNimbus job type goes to, and where each status lands. */
function Placement({ run }: { run: ImportRunSummary }) {
  const qc = useQueryClient();
  const workflows = useQuery({ queryKey: ["workflows"], queryFn: () => get<WorkflowItem[]>("/api/workflows") });
  const place = useQuery({ queryKey: ["import-placement", run.id, run.options], queryFn: () => get<{ type: string; status: string; insurance: boolean; count: number; workflow: string; stage: string }[]>(`/api/import/runs/${run.id}/placement`) });
  const save = useMutation({
    mutationFn: (o: object) => patch<ImportRunSummary>(`/api/import/runs/${run.id}/options`, o),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["import-runs"] }),
  });
  const editable = run.status === "ready";
  const active = workflows.data?.filter((w) => !w.archived) ?? [];
  const current = (type: string) => run.options.workflowByType?.[type] ?? place.data?.find((p) => p.type === type && !p.insurance)?.workflow;
  return (
    <Panel>
      <PanelHeader title="Where jobs go" description="Each JobNimbus job type goes to a workflow here; statuses go to the stage with the same meaning. Stages can be changed on each job afterward." />
      <div className="flex flex-col gap-4 px-5 py-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <SelectField
          label="Jobs with JobNimbus's “Insurance Claim” box checked"
          disabled={!editable}
          value={run.options.insuranceWorkflowId ?? ""}
          onChange={(e) => save.mutate({ insuranceWorkflowId: e.target.value || null })}
        >
          <option value="">Use their job type's workflow</option>
          {active.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </SelectField>
        <div className="grid gap-3 sm:grid-cols-2">
          {run.jobTypes.map((t) => (
            <SelectField
              key={t.name}
              label={`${t.name} (${t.count})`}
              disabled={!editable}
              value={run.options.workflowByType?.[t.name] ?? active.find((w) => w.name === current(t.name))?.id ?? ""}
              onChange={(e) => save.mutate({ workflowByType: { ...run.options.workflowByType, [t.name]: e.target.value } })}
            >
              {active.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </SelectField>
          ))}
        </div>
        {place.data && (
          <details className="text-sm">
            <summary className="cursor-pointer font-semibold text-ink-soft">How each status lands ({place.data.length})</summary>
            <table className="mt-2 w-full">
              <thead className="text-left text-xs text-muted uppercase">
                <tr>
                  <th className="py-1">JobNimbus</th>
                  <th className="py-1">Jobs</th>
                  <th className="py-1">Goes to</th>
                </tr>
              </thead>
              <tbody>
                {place.data.map((p, i) => (
                  <tr key={i} className="border-t border-mist">
                    <td className="py-1">
                      {p.type} · {p.status}
                      {p.insurance && <span className="text-muted"> (insurance)</span>}
                    </td>
                    <td className="py-1">{p.count}</td>
                    <td className="py-1">
                      {p.workflow} → {p.stage}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </div>
    </Panel>
  );
}

/** A colored dot per contact category, so the type reads at a glance. */
const CATEGORY_DOT: Record<string, string> = { customer: "bg-brand", subcontractor: "bg-wait", vendor: "bg-ok", other: "bg-muted" };

/** Under the type chips: which JobNimbus types make up the chosen CRM type. */
function groupSizeNote(g: { jnTypes: string[] } | undefined) {
  if (!g || g.jnTypes.length < 2) return null;
  return <span className="w-full text-xs text-muted">From JobNimbus types: {g.jnTypes.map((t) => t || "(no type)").join(", ")}</span>;
}

function Records({ run, kind }: { run: ImportRunSummary; kind: ImportKind }) {
  const qc = useQueryClient();
  const [flag, setFlag] = useState("");
  // Contacts: the CRM type they come in as (one CRM type can come from several JobNimbus types).
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  useEffect(() => {
    const t = setTimeout(() => setSearch(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    setOffset(0);
    setPicked(new Set());
  }, [flag, search, type]);
  const typeGroups = Object.values(
    run.contactTypes.reduce<Record<string, { typeName: string; category: string; count: number; jnTypes: string[] }>>((acc, t) => {
      const g = (acc[t.typeName] ??= { typeName: t.typeName, category: t.category, count: 0, jnTypes: [] });
      g.count += t.count;
      g.jnTypes.push(t.name);
      return acc;
    }, {}),
  ).sort((a, b) => b.count - a.count);
  const jnTypes = kind === "contact" && type ? typeGroups.find((g) => g.typeName === type)?.jnTypes : undefined;
  const params = new URLSearchParams({ kind, offset: String(offset), limit: "50", ...(flag ? { flag } : {}), ...(search ? { q: search } : {}), ...(jnTypes ? { jnTypes: jnTypes.join(",") } : {}) });
  const list = useQuery({ queryKey: ["import-records", run.id, kind, flag, type, search, offset, run.counts], queryFn: () => get<{ items: ImportRecordItem[]; more: boolean }>(`/api/import/runs/${run.id}/records?${params}`) });
  const decide = useMutation({
    mutationFn: (body: object) => post(`/api/import/runs/${run.id}/decide`, body),
    onSuccess: async () => {
      setPicked(new Set());
      await qc.invalidateQueries({ queryKey: ["import-runs"] });
      await qc.invalidateQueries({ queryKey: ["import-records", run.id] });
    },
  });
  const editable = run.status === "ready";
  const counts = run.counts[kind];
  const flags = Object.entries(counts.flags).sort((a, b) => b[1] - a[1]);
  const groupBody = { kind, ...(flag ? { flag } : {}), ...(jnTypes ? { jnTypes } : {}) };
  const groupSize = flag ? (flag === "none" ? counts.total - Object.values(counts.flags).reduce((s, n) => s + n, 0) : counts.flags[flag]) : counts.total;

  return (
    <Panel>
      <div className="flex flex-wrap items-center gap-2 border-b border-mist px-5 py-3">
        {[["", `All ${counts.total}`], ["none", "No problems"], ...flags.map(([f, n]) => [f, `${IMPORT_FLAG_LABELS[f] ?? f} ${n}`])].map(([f, label]) => (
          <button key={f} onClick={() => setFlag(f!)} className={cx("rounded-full px-3 py-1 text-sm font-semibold", flag === f ? "bg-ink text-white" : "bg-mist text-ink-soft hover:text-ink")}>
            {label}
          </button>
        ))}
        <Field label="" aria-label="Search" placeholder="Search names" value={q} onChange={(e) => setQ(e.target.value)} className="ml-auto w-56" />
      </div>
      {kind === "contact" && typeGroups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-mist px-5 py-3 text-sm">
          <span className="text-muted">Comes in as:</span>
          <button onClick={() => setType("")} className={cx("rounded-full px-3 py-1 font-semibold", !type ? "bg-ink text-white" : "bg-mist text-ink-soft hover:text-ink")}>
            Every type
          </button>
          {typeGroups.map((g) => (
            <button
              key={g.typeName}
              onClick={() => setType(type === g.typeName ? "" : g.typeName)}
              title={`From JobNimbus: ${g.jnTypes.map((t) => t || "(no type)").join(", ")}`}
              className={cx("flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold", type === g.typeName ? "bg-ink text-white" : "bg-mist text-ink-soft hover:text-ink")}
            >
              <span className={cx("size-2 rounded-full", CATEGORY_DOT[g.category] ?? "bg-muted")} />
              {g.typeName} {g.count.toLocaleString()}
            </button>
          ))}
          {type && groupSizeNote(typeGroups.find((g) => g.typeName === type))}
        </div>
      )}
      {editable && (
        <div className="flex flex-wrap items-center gap-2 border-b border-mist bg-paper px-5 py-2 text-sm">
          {picked.size ? (
            <>
              <span className="font-semibold">{picked.size} picked:</span>
              <Button className="px-3 py-1" onClick={() => decide.mutate({ ids: [...picked], decision: "import" })}>
                Import
              </Button>
              {kind === "contact" && (
                <Button className="px-3 py-1" onClick={() => decide.mutate({ ids: [...picked], decision: "merge" })}>
                  Merge as suggested
                </Button>
              )}
              <Button className="px-3 py-1" onClick={() => decide.mutate({ ids: [...picked], decision: "skip" })}>
                Skip
              </Button>
            </>
          ) : (
            <>
              <span className="text-muted">All {groupSize ?? 0} in this group:</span>
              <Button className="px-3 py-1" busy={decide.isPending} onClick={() => decide.mutate({ ...groupBody, decision: "import" })}>
                Import
              </Button>
              {kind === "contact" && (flag === "duplicate" || flag === "possible_duplicate" || flag === "matches_existing") && (
                <Button className="px-3 py-1" onClick={() => decide.mutate({ ...groupBody, decision: "merge" })}>
                  Merge as suggested
                </Button>
              )}
              <Button className="px-3 py-1" onClick={() => decide.mutate({ ...groupBody, decision: "skip" })}>
                Skip
              </Button>
            </>
          )}
          {decide.error && <span className="text-danger">{errorMessage(decide.error)}</span>}
        </div>
      )}
      {list.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : !list.data?.items.length ? (
        <Empty>Nothing here.</Empty>
      ) : (
        <ul>
          {list.data.items.map((r) => (
            <li key={r.id} className="flex items-start gap-3 border-b border-mist px-5 py-3 text-sm last:border-0">
              {editable && (
                <input
                  type="checkbox"
                  aria-label={`Pick ${r.label}`}
                  className="mt-1 size-4 accent-[var(--color-brand)]"
                  disabled={r.flags.includes("already_imported")}
                  checked={picked.has(r.id)}
                  onChange={(e) => {
                    const next = new Set(picked);
                    if (e.target.checked) next.add(r.id);
                    else next.delete(r.id);
                    setPicked(next);
                  }}
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{r.label}</span>
                  <Badge tone={DECISION[r.decision].tone}>{DECISION[r.decision].label}</Badge>
                  {r.flags.map((f) => (
                    <Badge key={f} tone="brand">
                      {IMPORT_FLAG_LABELS[f] ?? f}
                    </Badge>
                  ))}
                </div>
                {r.importsAs && (
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs">
                    <span className={cx("size-2 rounded-full", CATEGORY_DOT[r.importsAs.category] ?? "bg-muted")} />
                    <span className="font-semibold text-ink">Comes in as {r.importsAs.typeName}</span>
                    {r.importsAs.jnType && r.importsAs.jnType.toLowerCase() !== r.importsAs.typeName.toLowerCase() && <span className="text-muted">({r.importsAs.jnType} in JobNimbus)</span>}
                  </div>
                )}
                <div className="text-muted">{r.detail}</div>
                {r.mergeInto && <div className="text-ink-soft">{r.decision === "merge" ? "Merges into" : "Could merge into"} {r.mergeInto.label}{r.mergeInto.kind === "contact" ? " (in the CRM)" : " (from JobNimbus)"}</div>}
                {r.reasons.map((x, i) => (
                  <div key={i} className="text-ink-soft">
                    {x}
                  </div>
                ))}
                {r.error && (r.error.startsWith("Skipped") ? <div className="text-muted">{r.error}</div> : <div className="text-danger">Not imported: {r.error}</div>)}
              </div>
            </li>
          ))}
        </ul>
      )}
      {(offset > 0 || list.data?.more) && (
        <div className="flex justify-between px-5 py-3">
          <Button variant="quiet" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>
            Previous
          </Button>
          <Button variant="quiet" disabled={!list.data?.more} onClick={() => setOffset(offset + 50)}>
            Next
          </Button>
        </div>
      )}
    </Panel>
  );
}
